// SEAT-DRAWER-UX §2.4 (docs/design/2026-10-28-seat-drawer-ux.md): the drawer's Tasks tab below
// its stat line, for a manager or the seat's holder (the seat's `tasks.list`, the read the Tasks
// modal shares): Done in the last seven days, the open tasks as rows (a row opens Work's task
// drawer over the chart), and with nothing open the last task done. The New task / Give work
// dialog is Work's own, with the seat preset as the assignee.
import { Badge } from "@companyos/ui";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { digitalEmployeesQuery, seatsQuery } from "../queries";
import { QuickAddPreparing } from "../quick-add/preparing";
import { Loading } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";
import { localDay, useSeatPeople } from "../work/model";
import { NewTaskDialog } from "../work/work-dialogs";
import { boardsQuery, invalidateWork } from "../work/work-queries";
import { chartKeys } from "./chart-queries";
import { MoreRowsLink } from "./seat-item-row";
import { topOf } from "./seat-rows";
import { seatTasksKey, seatTasksQuery } from "./seat-section-modals";
import { SectionModal } from "./section-modal";

const WEEK_MS = 7 * 86_400_000;

export function SeatTaskRows({
  api,
  company,
  seatId,
  all,
  onOpenTask,
  onMore,
  empty,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  /** The All view: the first rows only. */
  all: boolean;
  onOpenTask: (id: string) => void;
  onMore: () => void;
  /** The empty state's first line and buttons (the panel's). */
  empty: ReactNode;
}) {
  const tasks = useQuery(seatTasksQuery(api, company.id, seatId));
  if (tasks.isPending) return <Loading label="Loading the seat's tasks" rows={1} />;
  if (tasks.isError) return <ErrorNotice what="The seat's tasks" error={tasks.error} />;
  const now = Date.now();
  const open = tasks.data.filter((t) => t.completed_at === null);
  const done = tasks.data
    .filter((t) => t.completed_at !== null)
    .sort((a, b) => (b.completed_at ?? "").localeCompare(a.completed_at ?? ""));
  const doneWeek = done.filter(
    (t) => now - new Date(t.completed_at ?? 0).getTime() <= WEEK_MS,
  ).length;
  const last = done[0];
  const today = localDay(new Date());
  const { shown, more } = topOf(open, all);
  return (
    <div className="flex flex-col gap-1.5" data-slot="seat-task-rows">
      <p className="text-xs text-muted-foreground tabular-nums" data-slot="tasks-done-week">
        Done, 7 days: {doneWeek}
      </p>
      {open.length === 0 ? (
        <div className="flex flex-col gap-1">
          {empty}
          {last ? (
            <p className="text-xs text-faint" data-slot="tasks-last-done">
              Last done: “{last.title}” · {formatDateTime(last.completed_at ?? "")}
            </p>
          ) : null}
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-1">
            {shown.map((t) => {
              const due = t.due_at ? localDay(t.due_at) : null;
              return (
                <li key={t.id} data-task={t.id} className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate rounded-sm text-left text-sm leading-5 text-secondary hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                    title={t.title}
                    data-slot="task-row"
                    onClick={() => onOpenTask(t.id)}
                  >
                    {t.title}
                  </button>
                  {due ? (
                    <Badge variant={due < today ? "danger" : "neutral"} className="shrink-0">
                      {due < today ? `Overdue · ${due}` : due}
                    </Badge>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <MoreRowsLink n={more} onClick={onMore} />
        </>
      )}
    </div>
  );
}

/** Work's new-task dialog, the seat preset as assignee; a create refreshes the seat and chart. */
export function SeatNewTask({
  api,
  company,
  seatId,
  open,
  onOpenChange,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const people = useSeatPeople(api, co);
  const boards = useQuery(boardsQuery(api, co));
  // The roster reads `useSeatPeople` waits on (the same cache entries), for their failure.
  const seats = useInfiniteQuery(seatsQuery(api, co));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  const openBoards = (boards.data?.data ?? []).filter((b) => !b.archived_at);
  if (!people.ready || !boards.data)
    return open ? (
      <QuickAddPreparing
        title="New task"
        failed={boards.isError || seats.isError || des.isError}
        retry={() => {
          if (boards.isError) void boards.refetch();
          if (seats.isError) void seats.refetch();
          if (des.isError) void des.refetch();
        }}
        onClose={() => onOpenChange(false)}
      />
    ) : null;
  if (openBoards.length === 0)
    return open ? (
      <SectionModal
        title="New task"
        onClose={() => onOpenChange(false)}
        slot="new-task-no-board"
        size="default"
      >
        <p className="text-sm text-muted-foreground">
          A task lives on a board, and the company has no open board yet. Create one in Work first.
        </p>
      </SectionModal>
    ) : null;
  return (
    <NewTaskDialog
      api={api}
      company={company}
      boards={openBoards}
      people={people}
      open={open}
      onOpenChange={onOpenChange}
      defaults={{ assignee: seatId }}
      onCreated={() => {
        void qc.invalidateQueries({ queryKey: seatTasksKey(co, seatId) });
        void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
        invalidateWork(qc, co);
      }}
    />
  );
}
